import React from 'react';
import { Link } from 'react-router-dom';
import { formatTimeAgo } from 'helpers/Datetime.helper';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import { IBbs } from 'api/interfaces/bbs.interface';
import { LoadingComponent } from 'commons/loading';
import MagazineListSkeleton from './MagazineListSkeleton';
import { getCurrentLanguageCode } from "helpers/String.helper";

interface MagazineListProps {
    magazineList: IBbs[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
}

// 다국어 텍스트 선택 헬퍼 함수
const getMultilingualText = (magazine: IBbs, field: 'title' | 'text') => {
    // multiTextList가 없거나 비어있으면 기본 텍스트 반환
    if (!magazine.multiTextList || magazine.multiTextList.length === 0) {
        return field === 'title' ? (magazine.title || magazine.subject) : magazine.text;
    }

    const currentLangCode = getCurrentLanguageCode();

    // 1순위: 현재 브라우저 언어와 일치하는 텍스트 찾기
    const currentLangText = magazine.multiTextList.find(
        multiText => (multiText as any).languageCode === currentLangCode
    );

    if (currentLangText) {
        return field === 'title' ? (currentLangText.title) : currentLangText.text;
    }

    // 2순위: 영어 텍스트 찾기
    const englishText = magazine.multiTextList.find(
        multiText => (multiText as any).languageCode === 'en'
    );

    if (englishText) {
        return field === 'title' ? (englishText.title) : englishText.text;
    }

    // 3순위: 기본 텍스트 (국문)
    return field === 'title' ? (magazine.title) : magazine.text;
};

const MagazineListComponent: React.FC<MagazineListProps> = ({ magazineList, isLoading, isLoadingMore, hasMore }) => {
    return (
        <main className="bg-gray-50 min-h-screen overflow-y-auto">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtMagazine")}
                </h1>
            </div>
            <div className="container mx-auto px-3 sm:px-4 lg:px-6 pt-4">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4">
                        {isLoading ? (
                            <MagazineListSkeleton />
                        ) : magazineList.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                </svg>
                                <p className="text-center">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoMagazine")}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <div className="space-y-3">
                                        {magazineList.map((magazine) => (
                                            <Link
                                                key={magazine.id}
                                                to={`/student/hub/magazine/${magazine.id}`}
                                                className="block bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="relative">
                                                            {magazine.isNew && (
                                                                <span className="absolute -top-1 -left-2 w-2 h-2 rounded-full bg-[#CF3688] z-10 border-2 border-white"></span>
                                                            )}
                                                            <h3 className="text-base font-semibold text-gray-900 line-clamp-2 mb-2">
                                                                {getMultilingualText(magazine, 'title')}
                                                            </h3>
                                                        </div>
                                                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                                                            {getMultilingualText(magazine, 'text')}
                                                        </p>
                                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                                            <span className="flex items-center gap-1">
                                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                                                </svg>
                                                                {magazine.likeCount}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                                </svg>
                                                                {magazine.commentCount}
                                                            </span>
                                                            <span>{formatTimeAgo(magazine.createdAt)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                {/* 추가 로딩 상태 */}
                                {isLoadingMore && (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="flex items-center space-x-2">
                                            <svg className="w-5 h-5 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className="text-sm text-gray-500">더 많은 매거진을 불러오는 중...</span>
                                        </div>
                                    </div>
                                )}

                                {/* 더 이상 로드할 데이터가 없을 때 */}
                                {!isLoadingMore && !hasMore && magazineList.length > 0 && (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default MagazineListComponent;
